import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, LogOut, RefreshCw, LayoutDashboard, Users, Rocket, MessageSquare, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, testimonialsApi, betaApi } from '../lib/api';

// Import refactored components
import {
  AdminStatsCards,
  AdminPlatformSettings,
  AdminLeadSettings,
  AdminBugTracker,
  AdminContactMessages,
  AdminUsersTable,
  AdminTestimonials,
  AdminBetaSignups,
  AdminPlanDistribution,
  AdminActiveGrants,
  GrantDialog,
  PasswordResetDialog,
  SuspendDialog,
  DeleteUserDialog,
} from '../components/admin';

export default function Admin() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [adminSecret, setAdminSecret] = useState('');
  const [loginSecret, setLoginSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [grantForm, setGrantForm] = useState({ plan_id: 'business', duration_months: 12 });
  const [granting, setGranting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Platform settings
  const [platformDomain, setPlatformDomain] = useState('');
  const [savingDomain, setSavingDomain] = useState(false);
  
  // Bug Tracker state
  const [bugTrackerSettings, setBugTrackerSettings] = useState({
    enabled: false,
    developer_email: '',
    email_notifications: true,
  });
  const [bugReports, setBugReports] = useState([]);
  const [bugStatusCounts, setBugStatusCounts] = useState({});
  const [bugFilter, setBugFilter] = useState({ status: '', priority: '' });
  const [savingBugSettings, setSavingBugSettings] = useState(false);
  const [updatingBug, setUpdatingBug] = useState(false);
  
  // Contact Messages state
  const [contactMessages, setContactMessages] = useState([]);
  const [contactStatusCounts, setContactStatusCounts] = useState({});
  const [contactFilter, setContactFilter] = useState({ status: '' });
  const [updatingContact, setUpdatingContact] = useState(false);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialStatusCounts, setTestimonialStatusCounts] = useState({});
  const [testimonialFilter, setTestimonialFilter] = useState('');
  const [updatingTestimonial, setUpdatingTestimonial] = useState(false);
  
  // Beta Signups state
  const [betaSignups, setBetaSignups] = useState([]);
  const [betaStatusCounts, setBetaStatusCounts] = useState({});
  const [betaPainPointStats, setBetaPainPointStats] = useState({});
  const [betaFilter, setBetaFilter] = useState('');
  const [updatingBetaSignup, setUpdatingBetaSignup] = useState(false);
  
  // Google Email integration state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [connectingGoogle, setConnectingGoogle] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Corbel Admin';
    return () => { document.title = 'Corbel CRM'; };
  }, []);

  useEffect(() => {
    const storedSecret = sessionStorage.getItem('adminSecret');
    if (storedSecret) {
      setAdminSecret(storedSecret);
      verifyAndLoad(storedSecret);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyAndLoad = async (secret) => {
    try {
      await adminApi.verify(secret);
      setAdminSecret(secret);
      setAuthenticated(true);
      sessionStorage.setItem('adminSecret', secret);
      await loadData(secret);
    } catch (error) {
      sessionStorage.removeItem('adminSecret');
      setAuthenticated(false);
      if (error.response?.status === 403) {
        toast.error('Invalid admin secret');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (secret) => {
    try {
      const [statsRes, usersRes, grantsRes, settingsRes, bugSettingsRes, bugReportsRes, contactMessagesRes, googleStatusRes, testimonialsRes, betaSignupsRes] = await Promise.all([
        adminApi.getStats(secret),
        adminApi.getUsers(secret),
        adminApi.listGrants(secret),
        adminApi.getPlatformSettings(secret),
        adminApi.getBugTrackerSettings(secret),
        adminApi.getBugReports(secret),
        adminApi.getContactMessages(secret),
        adminApi.getAdminGoogleStatus(secret),
        testimonialsApi.getAll(secret),
        betaApi.getSignups(secret),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setGrants(grantsRes.data.grants);
      setPlatformDomain(settingsRes.data.platform_domain || '');
      setBugTrackerSettings(bugSettingsRes.data);
      setBugReports(bugReportsRes.data.reports || []);
      setBugStatusCounts(bugReportsRes.data.status_counts || {});
      setContactMessages(contactMessagesRes.data.messages || []);
      setContactStatusCounts(contactMessagesRes.data.status_counts || {});
      setGoogleConnected(googleStatusRes.data.connected || false);
      setGoogleEmail(googleStatusRes.data.email || '');
      setTestimonials(testimonialsRes.data.testimonials || []);
      setTestimonialStatusCounts(testimonialsRes.data.status_counts || {});
      setBetaSignups(betaSignupsRes.data.signups || []);
      setBetaStatusCounts(betaSignupsRes.data.status_counts || {});
      setBetaPainPointStats(betaSignupsRes.data.pain_point_stats || {});
    } catch (error) {
      toast.error('Failed to load admin data');
    }
  };

  // Bug tracker handlers
  const loadBugReports = async () => {
    try {
      const response = await adminApi.getBugReports(adminSecret, bugFilter);
      setBugReports(response.data.reports || []);
      setBugStatusCounts(response.data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load bug reports');
    }
  };

  const handleSaveBugSettings = async () => {
    setSavingBugSettings(true);
    try {
      await adminApi.updateBugTrackerSettings(bugTrackerSettings, adminSecret);
      toast.success('Bug tracker settings saved');
    } catch (error) {
      toast.error('Failed to save bug tracker settings');
    } finally {
      setSavingBugSettings(false);
    }
  };

  const handleUpdateBugStatus = async (reportId, status, notes) => {
    setUpdatingBug(true);
    try {
      await adminApi.updateBugReport(reportId, { status, admin_notes: notes }, adminSecret);
      toast.success('Bug report updated');
      loadBugReports();
    } catch (error) {
      toast.error('Failed to update bug report');
    } finally {
      setUpdatingBug(false);
    }
  };

  const handleDeleteBug = async (reportId) => {
    if (!window.confirm('Delete this bug report?')) return;
    try {
      await adminApi.deleteBugReport(reportId, adminSecret);
      toast.success('Bug report deleted');
      loadBugReports();
    } catch (error) {
      toast.error('Failed to delete bug report');
    }
  };

  // Contact messages handlers
  const loadContactMessages = async () => {
    try {
      const response = await adminApi.getContactMessages(adminSecret, contactFilter);
      setContactMessages(response.data.messages || []);
      setContactStatusCounts(response.data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load contact messages');
    }
  };

  const handleUpdateContactStatus = async (messageId, status, notes) => {
    setUpdatingContact(true);
    try {
      await adminApi.updateContactMessage(messageId, { status, admin_notes: notes }, adminSecret);
      toast.success('Message updated');
      loadContactMessages();
    } catch (error) {
      toast.error('Failed to update message');
    } finally {
      setUpdatingContact(false);
    }
  };

  const handleDeleteContact = async (messageId) => {
    if (!window.confirm('Delete this contact message?')) return;
    try {
      await adminApi.deleteContactMessage(messageId, adminSecret);
      toast.success('Message deleted');
      loadContactMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Testimonials handlers
  const loadTestimonials = async () => {
    try {
      const response = await testimonialsApi.getAll(adminSecret, testimonialFilter);
      setTestimonials(response.data.testimonials || []);
      setTestimonialStatusCounts(response.data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load testimonials');
    }
  };

  const handleUpdateTestimonial = async (id, data) => {
    setUpdatingTestimonial(true);
    try {
      await testimonialsApi.update(id, data, adminSecret);
      toast.success('Testimonial updated');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    } finally {
      setUpdatingTestimonial(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await testimonialsApi.delete(id, adminSecret);
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  // Beta Signups handlers
  const loadBetaSignups = async () => {
    try {
      const response = await betaApi.getSignups(adminSecret, betaFilter);
      setBetaSignups(response.data.signups || []);
      setBetaStatusCounts(response.data.status_counts || {});
      setBetaPainPointStats(response.data.pain_point_stats || {});
    } catch (error) {
      toast.error('Failed to load beta signups');
    }
  };

  const handleUpdateBetaSignup = async (id, data) => {
    setUpdatingBetaSignup(true);
    try {
      await betaApi.updateSignup(id, data, adminSecret);
      toast.success('Signup updated');
      loadBetaSignups();
    } catch (error) {
      toast.error('Failed to update signup');
    } finally {
      setUpdatingBetaSignup(false);
    }
  };

  const handleDeleteBetaSignup = async (id) => {
    if (!window.confirm('Delete this signup?')) return;
    try {
      await betaApi.deleteSignup(id, adminSecret);
      toast.success('Signup deleted');
      loadBetaSignups();
    } catch (error) {
      toast.error('Failed to delete signup');
    }
  };

  const handleExportBetaSignups = async () => {
    try {
      const response = await betaApi.exportSignups(adminSecret);
      return response.data;
    } catch (error) {
      toast.error('Export failed');
      return null;
    }
  };

  const handleApproveBetaSignup = async (id) => {
    const response = await betaApi.approveSignup(id, adminSecret);
    loadBetaSignups();
    return response.data;
  };

  // Auth handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    await verifyAndLoad(loginSecret);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminSecret');
    setAuthenticated(false);
    setAdminSecret('');
    setLoginSecret('');
  };

  // Platform settings handler
  const handleSavePlatformDomain = async () => {
    if (!platformDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }
    setSavingDomain(true);
    try {
      await adminApi.updatePlatformDomain(platformDomain.trim(), adminSecret);
      toast.success('Platform domain updated! Lead form URLs will now use this domain.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update domain');
    } finally {
      setSavingDomain(false);
    }
  };

  // Google Email integration handlers
  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const response = await adminApi.getAdminGoogleAuthUrl(adminSecret);
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get Google auth URL');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google account? Email notifications will stop working.')) return;
    try {
      await adminApi.disconnectAdminGoogle(adminSecret);
      setGoogleConnected(false);
      setGoogleEmail('');
      toast.success('Google account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Google account');
    }
  };

  // User management handlers
  const handleSearch = async () => {
    try {
      const response = await adminApi.getUsers(adminSecret, searchTerm);
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleGrantClick = (user) => {
    setSelectedUser(user);
    setShowGrantDialog(true);
  };

  const handlePasswordResetClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const handlePasswordReset = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPassword(true);
    try {
      await adminApi.resetPassword({
        user_email: selectedUser.email,
        new_password: newPassword,
        admin_secret: adminSecret,
      });
      toast.success(`Password reset for ${selectedUser.email}`);
      setShowPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (err) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          toast.success('Copied to clipboard');
        } else {
          toast.info(`Value: ${text}`, { duration: 10000 });
        }
      } catch (fallbackErr) {
        toast.info(`Value: ${text}`, { duration: 10000 });
      }
    }
  };

  const handleGrantSubmit = async () => {
    if (!selectedUser) return;
    setGranting(true);
    try {
      await adminApi.grantSubscription({
        user_email: selectedUser.email,
        plan_id: grantForm.plan_id,
        duration_months: grantForm.duration_months,
        admin_secret: adminSecret,
      });
      toast.success(`Granted ${grantForm.plan_id} plan to ${selectedUser.email}`);
      setShowGrantDialog(false);
      await loadData(adminSecret);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant subscription');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (userEmail) => {
    if (!window.confirm(`Revoke subscription grant from ${userEmail}?`)) return;
    try {
      await adminApi.revokeGrant(userEmail, adminSecret);
      toast.success('Grant revoked');
      await loadData(adminSecret);
    } catch (error) {
      toast.error('Failed to revoke grant');
    }
  };

  const handleSuspendClick = (user) => {
    setSelectedUser(user);
    setSuspensionReason('');
    setShowSuspendDialog(true);
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;
    setSuspending(true);
    try {
      await adminApi.suspendAccount(selectedUser.email, adminSecret, suspensionReason);
      toast.success(`Account suspended: ${selectedUser.email}`);
      setShowSuspendDialog(false);
      await loadData(adminSecret);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend account');
    } finally {
      setSuspending(false);
    }
  };

  const handleReactivate = async (userEmail) => {
    if (!window.confirm(`Reactivate account for ${userEmail}?`)) return;
    try {
      await adminApi.reactivateAccount(userEmail, adminSecret);
      toast.success('Account reactivated');
      await loadData(adminSecret);
    } catch (error) {
      toast.error('Failed to reactivate account');
    }
  };

  const handleCancelSubscription = async (userEmail) => {
    if (!window.confirm(`Cancel subscription for ${userEmail}? This will remove all paid features.`)) return;
    try {
      await adminApi.cancelSubscription(userEmail, adminSecret);
      toast.success('Subscription cancelled');
      await loadData(adminSecret);
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setDeleting(true);
    try {
      await adminApi.deleteUser(selectedUser.email, adminSecret);
      toast.success(`User ${selectedUser.email} deleted successfully`);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      await loadData(adminSecret);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-sm border-slate-700 bg-slate-800">
          <CardHeader className="text-center">
            <div className="mx-auto h-16 w-16 bg-orange-500 rounded-sm flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Admin Dashboard</CardTitle>
            <CardDescription className="text-slate-400">
              Enter admin secret to access the dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-slate-300">Admin Secret</Label>
                <Input
                  type="password"
                  value={loginSecret}
                  onChange={(e) => setLoginSecret(e.target.value)}
                  placeholder="Enter admin secret"
                  className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white"
                  data-testid="admin-secret-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-sm bg-orange-500 hover:bg-orange-600"
                disabled={loading || !loginSecret}
                data-testid="admin-login-btn"
              >
                {loading ? 'Verifying...' : 'Access Dashboard'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white"
                onClick={() => navigate('/login')}
              >
                Back to App Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  const TABS = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'beta', label: 'Beta Testers', icon: Rocket, badge: betaStatusCounts.new || 0 },
    { id: 'content', label: 'Content', icon: MessageSquare, badge: (bugStatusCounts.new || 0) + (contactStatusCounts.new || 0) },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-orange-500 rounded-sm flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Corbel Admin</h1>
              <p className="text-xs text-slate-400">Subscription & User Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadData(adminSecret)}
              className="text-slate-300 hover:text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-300 hover:text-white"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 -mb-px" data-testid="admin-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-orange-600 text-white">{tab.badge}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <AdminStatsCards stats={stats} />
            <AdminPlanDistribution stats={stats} />
            <AdminActiveGrants grants={grants} onRevoke={handleRevoke} formatDate={formatDate} />
          </>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <>
            <AdminPlanDistribution stats={stats} />
            <AdminUsersTable
              users={users}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onSearch={handleSearch}
              onGrantClick={handleGrantClick}
              onRevoke={handleRevoke}
              onPasswordResetClick={handlePasswordResetClick}
              onSuspendClick={handleSuspendClick}
              onReactivate={handleReactivate}
              onCancelSubscription={handleCancelSubscription}
              onDeleteClick={handleDeleteClick}
              formatDate={formatDate}
            />
          </>
        )}

        {/* BETA TESTERS TAB */}
        {activeTab === 'beta' && (
          <AdminBetaSignups
            signups={betaSignups}
            statusCounts={betaStatusCounts}
            painPointStats={betaPainPointStats}
            filter={betaFilter}
            setFilter={setBetaFilter}
            onLoadSignups={loadBetaSignups}
            onUpdateSignup={handleUpdateBetaSignup}
            updatingSignup={updatingBetaSignup}
            onDeleteSignup={handleDeleteBetaSignup}
            onExport={handleExportBetaSignups}
            onApproveSignup={handleApproveBetaSignup}
          />
        )}

        {/* CONTENT TAB */}
        {activeTab === 'content' && (
          <>
            <AdminBugTracker
              settings={bugTrackerSettings}
              setSettings={setBugTrackerSettings}
              reports={bugReports}
              statusCounts={bugStatusCounts}
              filter={bugFilter}
              setFilter={setBugFilter}
              onSaveSettings={handleSaveBugSettings}
              savingSettings={savingBugSettings}
              onLoadReports={loadBugReports}
              onUpdateBugStatus={handleUpdateBugStatus}
              updatingBug={updatingBug}
              onDeleteBug={handleDeleteBug}
            />
            <AdminContactMessages
              messages={contactMessages}
              statusCounts={contactStatusCounts}
              filter={contactFilter}
              setFilter={setContactFilter}
              onLoadMessages={loadContactMessages}
              onUpdateStatus={handleUpdateContactStatus}
              updatingMessage={updatingContact}
              onDeleteMessage={handleDeleteContact}
            />
            <AdminTestimonials
              testimonials={testimonials}
              statusCounts={testimonialStatusCounts}
              filter={testimonialFilter}
              setFilter={setTestimonialFilter}
              onLoadTestimonials={loadTestimonials}
              onUpdateTestimonial={handleUpdateTestimonial}
              updatingTestimonial={updatingTestimonial}
              onDeleteTestimonial={handleDeleteTestimonial}
            />
          </>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <>
            <AdminPlatformSettings
              platformDomain={platformDomain}
              setPlatformDomain={setPlatformDomain}
              onSave={handleSavePlatformDomain}
              saving={savingDomain}
              googleConnected={googleConnected}
              googleEmail={googleEmail}
              onConnectGoogle={handleConnectGoogle}
              onDisconnectGoogle={handleDisconnectGoogle}
              connectingGoogle={connectingGoogle}
            />
            <AdminLeadSettings adminSecret={adminSecret} />
          </>
        )}
      </main>

      {/* Dialogs */}
      <GrantDialog
        open={showGrantDialog}
        onOpenChange={setShowGrantDialog}
        selectedUser={selectedUser}
        grantForm={grantForm}
        setGrantForm={setGrantForm}
        onSubmit={handleGrantSubmit}
        submitting={granting}
      />

      <PasswordResetDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
        selectedUser={selectedUser}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onGeneratePassword={generateRandomPassword}
        onSubmit={handlePasswordReset}
        submitting={resettingPassword}
        onCopyToClipboard={copyToClipboard}
      />

      <SuspendDialog
        open={showSuspendDialog}
        onOpenChange={setShowSuspendDialog}
        selectedUser={selectedUser}
        reason={suspensionReason}
        setReason={setSuspensionReason}
        onSubmit={handleSuspend}
        submitting={suspending}
      />

      <DeleteUserDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        selectedUser={selectedUser}
        onSubmit={handleDelete}
        submitting={deleting}
      />
    </div>
  );
}
